import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Ship, MapPin, Calendar, Euro, Users, Anchor, ChevronDown, ChevronUp, Copy, ImageIcon, CreditCard } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Departure row component ───
const DepartureRow = ({ dep, onUpdate, onDelete, token }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...dep });
  const [saving, setSaving] = useState(false);

  const statusColors = {
    available: 'bg-green-100 text-green-700',
    few_spots: 'bg-yellow-100 text-yellow-700',
    full: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  const statusLabels = { available: 'Disponible', few_spots: 'Dernières places', full: 'Complet', cancelled: 'Annulé' };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cruise-dates/${dep.id}`, {
        start_date: form.start_date,
        end_date: form.end_date,
        cabin_price: parseFloat(form.cabin_price),
        privatization_price: parseFloat(form.privatization_price),
        available_cabins: parseInt(form.available_cabins),
        catamaran: form.catamaran,
        status: form.status,
        is_public: form.is_public !== false,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Départ mis à jour');
      setEditing(false);
      onUpdate();
    } catch { toast.error('Erreur lors de la mise à jour'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce départ ?')) return;
    try {
      await axios.delete(`${API}/cruise-dates/${dep.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Départ supprimé');
      onDelete();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  if (editing) {
    return (
      <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-4 space-y-3" data-testid={`departure-edit-${dep.id}`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Date début</Label>
            <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Date fin</Label>
            <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Prix cabine (€/pers)</Label>
            <Input type="number" value={form.cabin_price} onChange={e => {
              const val = e.target.value;
              setForm(f => ({ ...f, cabin_price: val, privatization_price: Math.round(parseFloat(val || 0) * 8) }));
            }} />
          </div>
          <div>
            <Label className="text-xs">Prix privatisation (€) <span className="text-[#94A3B8]">= x8</span></Label>
            <Input type="number" value={form.privatization_price} onChange={e => setForm(f => ({ ...f, privatization_price: e.target.value }))} />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Cabines dispo</Label>
            <Input type="number" min="0" max="10" value={form.available_cabins} onChange={e => setForm(f => ({ ...f, available_cabins: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Catamaran</Label>
            <Input value={form.catamaran} onChange={e => setForm(f => ({ ...f, catamaran: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Statut</Label>
            <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Disponible</SelectItem>
                <SelectItem value="few_spots">Dernières places</SelectItem>
                <SelectItem value="full">Complet</SelectItem>
                <SelectItem value="cancelled">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Switch id={`public-${dep.id}`} checked={form.is_public !== false} onCheckedChange={c => setForm(f => ({ ...f, is_public: c }))} />
            <Label htmlFor={`public-${dep.id}`} className="text-xs">{form.is_public !== false ? 'Public' : 'Privé'}</Label>
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white" onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg px-3 sm:px-4 py-3 hover:bg-[#F8F9FA] transition-colors" data-testid={`departure-row-${dep.id}`}>
      <div className="flex items-start sm:items-center justify-between gap-2">
        <div className="flex items-center gap-3 sm:gap-6 flex-wrap text-xs sm:text-sm min-w-0">
          <div className="flex items-center gap-2 font-semibold text-[#0A2540]">
            <Calendar className="h-4 w-4 text-[#C4724F] shrink-0" />
            <span className="whitespace-nowrap">{formatDate(dep.start_date)} → {formatDate(dep.end_date)}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[#4A5568]">
            <span><b>{dep.cabin_price}€</b>/cab</span>
            <span>Privat: <b>{dep.privatization_price}€</b></span>
            <span><b>{dep.available_cabins}</b> dispo</span>
          </div>
          <Badge className={statusColors[dep.status] || 'bg-gray-100'}>{statusLabels[dep.status] || dep.status}</Badge>
          {dep.is_public === false && <Badge className="bg-red-50 text-red-600 border border-red-200">Privé</Badge>}
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setForm({ ...dep }); setEditing(true); }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Main component ───
const AdminCruises = () => {
  const [cruises, setCruises] = useState([]);
  const [departures, setDepartures] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingCruise, setEditingCruise] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedCruise, setExpandedCruise] = useState(null);
  const [addingDeparture, setAddingDeparture] = useState(null);
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    name: '', slug: '', destination: '', description: '', short_description: '',
    duration: '8 jours / 7 nuits', departure_port: 'Ajaccio',
    base_price: 0, privatization_base_price: 0,
    is_cabin_booking: true, is_privatization: true, is_on_request: false, is_active: true,
    itinerary: [], hero_image: '', boarding_pass_image: '',
  });
  const [uploadingImage, setUploadingImage] = useState(null);
  const [customDestMode, setCustomDestMode] = useState(false);

  const [firstDeparture, setFirstDeparture] = useState({
    start_date: '', end_date: '', cabin_price: '', privatization_price: '',
    available_cabins: '4', catamaran: 'LAGOON 46',
  });

  const [newDep, setNewDep] = useState({
    start_date: '', end_date: '', cabin_price: '', privatization_price: '',
    available_cabins: '4', catamaran: 'LAGOON 46',
  });

  useEffect(() => { fetchCruises(); }, [token]);

  const fetchCruises = async () => {
    try {
      const res = await axios.get(`${API}/cruises?active_only=false`);
      setCruises(res.data);
      // Auto-load departures for all cruises
      for (const cruise of res.data) {
        fetchDepartures(cruise.slug);
      }
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const fetchDepartures = async (slug) => {
    try {
      const res = await axios.get(`${API}/cruise-dates/${slug}`);
      setDepartures(prev => ({ ...prev, [slug]: res.data }));
    } catch { toast.error('Erreur de chargement des départs'); }
  };

  const toggleExpand = (cruise) => {
    if (expandedCruise === cruise.slug) {
      setExpandedCruise(null);
    } else {
      setExpandedCruise(cruise.slug);
      if (!departures[cruise.slug]) fetchDepartures(cruise.slug);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleSwitchChange = (name, checked) => setFormData(prev => ({ ...prev, [name]: checked }));

  const generateSlug = (name) =>
    name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({ ...prev, name, slug: editingCruise ? prev.slug : generateSlug(name) }));
  };

  const resetForm = () => {
    setFormData({ name: '', slug: '', destination: '', description: '', short_description: '', duration: '8 jours / 7 nuits', departure_port: 'Ajaccio', base_price: 0, privatization_base_price: 0, is_cabin_booking: true, is_privatization: true, is_on_request: false, is_active: true, itinerary: [], hero_image: '', boarding_pass_image: '' });
    setFirstDeparture({ start_date: '', end_date: '', cabin_price: '', privatization_price: '', available_cabins: '4', catamaran: 'LAGOON 46' });
    setEditingCruise(null);
  };

  const handleEdit = (cruise) => {
    setEditingCruise(cruise);
    setFormData({
      name: cruise.name, slug: cruise.slug, destination: cruise.destination,
      description: cruise.description, short_description: cruise.short_description,
      duration: cruise.duration, departure_port: cruise.departure_port,
      base_price: cruise.base_price, privatization_base_price: cruise.privatization_base_price,
      is_cabin_booking: cruise.is_cabin_booking, is_privatization: cruise.is_privatization, is_on_request: !!cruise.is_on_request, is_active: cruise.is_active,
      itinerary: cruise.itinerary || [],
      hero_image: cruise.hero_image || (cruise.images?.[0]) || '',
      boarding_pass_image: cruise.boarding_pass_image || '',
    });
    setIsDialogOpen(true);
    setCustomDestMode(false);
  };

  const handleImageUpload = async (file, field) => {
    setUploadingImage(field);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/cruises/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setFormData(prev => ({ ...prev, [field]: res.data.url }));
      toast.success(field === 'hero_image' ? 'Photo mise à jour' : 'Carte d\'embarquement mise à jour');
    } catch { toast.error('Erreur upload'); }
    finally { setUploadingImage(null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCruise) {
        const saveData = { ...formData };
        if (saveData.hero_image) {
          saveData.images = [saveData.hero_image];
        }
        await axios.put(`${API}/cruises/${editingCruise.id}`, saveData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Croisière mise à jour');
      } else {
        const res = await axios.post(`${API}/cruises`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Croisière créée');
        // Auto-create first departure if dates provided
        if (firstDeparture.start_date && firstDeparture.end_date) {
          const cruiseId = res.data.id;
          const cruiseSlug = formData.slug;
          try {
            await axios.post(`${API}/cruise-dates`, {
              cruise_id: cruiseId,
              cruise_slug: cruiseSlug,
              start_date: firstDeparture.start_date,
              end_date: firstDeparture.end_date,
              cabin_price: parseFloat(firstDeparture.cabin_price) || parseFloat(formData.base_price) || 0,
              privatization_price: parseFloat(firstDeparture.privatization_price) || parseFloat(formData.privatization_base_price) || 0,
              available_cabins: parseInt(firstDeparture.available_cabins) || 4,
              catamaran: firstDeparture.catamaran || 'LAGOON 46',
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Premier depart ajoute au calendrier');
          } catch { toast.error('Croisiere creee mais erreur ajout du depart'); }
        }
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCruises();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const handleDelete = async (cruiseId) => {
    if (!window.confirm('Supprimer cette croisière ?')) return;
    try {
      await axios.delete(`${API}/cruises/${cruiseId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Croisière supprimée');
      fetchCruises();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleDuplicate = async (cruiseId) => {
    try {
      const res = await axios.post(`${API}/cruises/${cruiseId}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Croisière dupliquée : ${res.data.name} (${res.data.duplicated_dates || 0} départs copiés)`);
      fetchCruises();
    } catch { toast.error('Erreur lors de la duplication'); }
  };

  const handleAddDeparture = async (cruise) => {
    try {
      await axios.post(`${API}/cruise-dates`, {
        cruise_id: cruise.id,
        cruise_slug: cruise.slug,
        start_date: newDep.start_date,
        end_date: newDep.end_date,
        cabin_price: parseFloat(newDep.cabin_price) || cruise.base_price,
        privatization_price: parseFloat(newDep.privatization_price) || cruise.privatization_base_price,
        available_cabins: parseInt(newDep.available_cabins) || 4,
        catamaran: newDep.catamaran,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Départ ajouté');
      setAddingDeparture(null);
      setNewDep({ start_date: '', end_date: '', cabin_price: '', privatization_price: '', available_cabins: '4', catamaran: 'LAGOON 46' });
      fetchDepartures(cruise.slug);
    } catch { toast.error("Erreur lors de l'ajout du départ"); }
  };

  return (
    <AdminLayout title="Gestion des croisières">
      <div className="space-y-6" data-testid="admin-cruises">
        <div className="flex justify-between items-center">
          <p className="text-[#4A5568]">{cruises.length} croisière{cruises.length > 1 ? 's' : ''}</p>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" data-testid="new-cruise-btn">
                <Plus className="mr-2 h-4 w-4" />Nouvelle croisière
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCruise ? 'Modifier la croisière' : 'Nouvelle croisière'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Nom *</Label><Input name="name" value={formData.name} onChange={handleNameChange} required /></div>
                  <div><Label>Slug (URL) *</Label><Input name="slug" value={formData.slug} onChange={handleInputChange} required /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Destination *</Label>
                    {customDestMode ? (
                      <div className="flex gap-2">
                        <Input placeholder="Saisir la destination" value={formData.destination}
                          onChange={e => setFormData(prev => ({ ...prev, destination: e.target.value }))} className="flex-1" autoFocus />
                        <Button type="button" variant="outline" size="sm" onClick={() => setCustomDestMode(false)}>Liste</Button>
                      </div>
                    ) : (
                      <Select value={formData.destination} onValueChange={(val) => {
                        if (val === '__custom') {
                          setCustomDestMode(true);
                          setFormData(prev => ({ ...prev, destination: '' }));
                          return;
                        }
                        setFormData(prev => ({ ...prev, destination: val }));
                        const source = cruises.find(c => c.destination === val && c.itinerary?.length > 0 && c.id !== editingCruise?.id);
                        if (source && (!formData.itinerary?.length || window.confirm(`Charger le programme de "${source.name}" ?`))) {
                          setFormData(prev => ({ ...prev, destination: val, itinerary: [...source.itinerary] }));
                          toast.success(`Programme chargé depuis "${source.name}"`);
                        }
                      }}>
                        <SelectTrigger><SelectValue placeholder="Choisir une destination" /></SelectTrigger>
                        <SelectContent>
                          {[...new Set(cruises.map(c => c.destination).filter(Boolean))].map(d => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                          <SelectItem value="__custom">Autre (saisie libre)...</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div><Label>Port de depart *</Label><Input name="departure_port" value={formData.departure_port} onChange={handleInputChange} required /></div>
                </div>
                <div><Label>Description courte *</Label><Input name="short_description" value={formData.short_description} onChange={handleInputChange} required /></div>
                <div><Label>Description complete *</Label><Textarea name="description" value={formData.description} onChange={handleInputChange} rows={4} required /></div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><Label>Duree</Label><Input name="duration" value={formData.duration} onChange={handleInputChange} /></div>
                  <div><Label>Prix cabine de base (€/pers)</Label><Input name="base_price" type="number" value={formData.base_price} onChange={(e) => {
                    const val = e.target.value;
                    setFormData(prev => ({ ...prev, base_price: val, privatization_base_price: Math.round(parseFloat(val || 0) * 8) }));
                  }} /></div>
                  <div><Label>Prix privatisation de base (€) <span className="text-xs text-[#94A3B8]">= x8</span></Label><Input name="privatization_base_price" type="number" value={formData.privatization_base_price} onChange={handleInputChange} /></div>
                </div>
                <div className="flex flex-wrap gap-4 sm:gap-8">
                  <div className="flex items-center gap-2">
                    <Switch id="is_cabin_booking" checked={formData.is_cabin_booking} onCheckedChange={c => handleSwitchChange('is_cabin_booking', c)} />
                    <Label htmlFor="is_cabin_booking">Réservation cabine</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="is_privatization" checked={formData.is_privatization} onCheckedChange={c => handleSwitchChange('is_privatization', c)} />
                    <Label htmlFor="is_privatization">Privatisation</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="is_on_request" checked={!!formData.is_on_request} onCheckedChange={c => handleSwitchChange('is_on_request', c)} />
                    <Label htmlFor="is_on_request" className="flex flex-col">
                      <span>Sur demande uniquement</span>
                      <span className="text-[10px] text-[#94A3B8] font-normal">Si activé : masque les dates de départ sur le site public</span>
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="is_active" checked={formData.is_active} onCheckedChange={c => handleSwitchChange('is_active', c)} />
                    <Label htmlFor="is_active">Actif</Label>
                  </div>
                </div>

                {/* Warning when on-request mode is enabled */}
                {formData.is_on_request && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="on-request-warning">
                    <span className="text-amber-600 text-lg leading-none mt-0.5">⚠️</span>
                    <div className="text-xs text-amber-900">
                      <strong>Mode "Sur demande" activé</strong> — Les dates de départ ajoutées dans l'onglet "Dates" ne seront pas affichées sur la page publique de cette croisière.
                      Décochez cette option si vous souhaitez vendre cette croisière à dates fixes.
                    </div>
                  </div>
                )}

                {/* Image uploads */}
                <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
                  <Label className="text-sm font-semibold text-[#0A2540] flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[#C4724F]" /> Photos</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Hero image */}
                    <div>
                      <p className="text-xs text-[#64748B] mb-1.5">Photo principale de la croisière</p>
                      {formData.hero_image && (
                        <img src={formData.hero_image} alt="Hero" className="w-full h-28 object-cover rounded-lg mb-2 border" />
                      )}
                      <label className={`flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-[#C4724F] transition-colors ${uploadingImage === 'hero_image' ? 'opacity-50' : ''}`}>
                        <ImageIcon className="h-4 w-4 text-[#94A3B8]" />
                        <span className="text-xs text-[#64748B]">{uploadingImage === 'hero_image' ? 'Upload...' : 'Changer la photo'}</span>
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                          onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'hero_image')} disabled={uploadingImage === 'hero_image'} />
                      </label>
                    </div>
                    {/* Boarding pass image */}
                    <div>
                      <p className="text-xs text-[#64748B] mb-1.5">Carte d'embarquement</p>
                      {formData.boarding_pass_image && (
                        <img src={formData.boarding_pass_image} alt="Carte embarquement" className="w-full h-28 object-cover rounded-lg mb-2 border" />
                      )}
                      <label className={`flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-[#C4724F] transition-colors ${uploadingImage === 'boarding_pass_image' ? 'opacity-50' : ''}`}>
                        <CreditCard className="h-4 w-4 text-[#94A3B8]" />
                        <span className="text-xs text-[#64748B]">{uploadingImage === 'boarding_pass_image' ? 'Upload...' : 'Changer la carte'}</span>
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                          onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'boarding_pass_image')} disabled={uploadingImage === 'boarding_pass_image'} />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Itinerary editor */}
                <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#C4724F]" />
                      <Label className="text-sm font-semibold text-[#0A2540]">Programme jour par jour</Label>
                      <span className="text-xs text-[#94A3B8]">{(formData.itinerary || []).length} jour(s)</span>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => {
                        const dest = formData.destination.toLowerCase();
                        const source = cruises.find(c => c.destination?.toLowerCase() === dest && c.itinerary?.length > 0 && c.id !== editingCruise?.id);
                        if (source) {
                          setFormData(prev => ({ ...prev, itinerary: [...source.itinerary] }));
                          toast.success(`Programme chargé depuis "${source.name}"`);
                        } else {
                          toast.error('Aucun programme par défaut trouvé pour cette destination');
                        }
                      }}>Charger programme par défaut</Button>
                      <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => {
                        setFormData(prev => ({ ...prev, itinerary: [...(prev.itinerary || []), { title: '', description: '' }] }));
                      }}><Plus className="h-3 w-3 mr-1" /> Ajouter un jour</Button>
                    </div>
                  </div>
                  {(formData.itinerary || []).length > 0 && (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {formData.itinerary.map((day, i) => (
                        <div key={i} className="flex gap-2 items-start bg-gray-50 rounded-lg p-2">
                          <div className="bg-[#C4724F] text-white text-xs font-bold rounded w-7 h-7 flex items-center justify-center shrink-0 mt-1">J{i + 1}</div>
                          <div className="flex-1 space-y-1">
                            <Input placeholder="Titre (ex: Ajaccio → Bonifacio)" value={day.title || ''} className="text-xs h-8"
                              onChange={e => { const it = [...formData.itinerary]; it[i] = { ...it[i], title: e.target.value }; setFormData(prev => ({ ...prev, itinerary: it })); }} />
                            <Textarea placeholder="Description de la journée..." value={day.description || ''} rows={2} className="text-xs min-h-[50px]"
                              onChange={e => { const it = [...formData.itinerary]; it[i] = { ...it[i], description: e.target.value }; setFormData(prev => ({ ...prev, itinerary: it })); }} />
                          </div>
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 shrink-0 mt-1"
                            onClick={() => { const it = formData.itinerary.filter((_, j) => j !== i); setFormData(prev => ({ ...prev, itinerary: it })); }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* First departure section - only for new cruises */}
                {!editingCruise && (
                  <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#C4724F]" />
                      <Label className="text-sm font-semibold text-[#0A2540]">Premier depart (optionnel)</Label>
                      <span className="text-xs text-[#94A3B8]">Apparaitra dans le Planning</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Date debut</Label>
                        <Input type="date" value={firstDeparture.start_date}
                          onChange={e => setFirstDeparture(f => ({ ...f, start_date: e.target.value }))}
                          data-testid="first-dep-start" />
                      </div>
                      <div>
                        <Label className="text-xs">Date fin</Label>
                        <Input type="date" value={firstDeparture.end_date}
                          onChange={e => setFirstDeparture(f => ({ ...f, end_date: e.target.value }))}
                          data-testid="first-dep-end" />
                      </div>
                      <div>
                        <Label className="text-xs">Prix cabine (€/pers)</Label>
                        <Input type="number" placeholder="Herite du prix de base"
                          value={firstDeparture.cabin_price}
                          onChange={e => {
                            const val = e.target.value;
                            setFirstDeparture(f => ({ ...f, cabin_price: val, privatization_price: String(Math.round(parseFloat(val || 0) * 8)) }));
                          }} />
                      </div>
                      <div>
                        <Label className="text-xs">Prix privat. (€)</Label>
                        <Input type="number" placeholder="= x8"
                          value={firstDeparture.privatization_price}
                          onChange={e => setFirstDeparture(f => ({ ...f, privatization_price: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>Annuler</Button>
                  <Button type="submit" className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white">{editingCruise ? 'Mettre à jour' : 'Créer'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-slate-200 rounded-lg animate-pulse" />)}</div>
        ) : (
          <div className="space-y-4">
            {cruises.map((cruise) => (
              <Card key={cruise.id} className={!cruise.is_active ? 'opacity-60' : ''} data-testid={`cruise-card-${cruise.slug}`}>
                <CardContent className="p-0">
                  {/* Cruise header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 gap-3">
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0A2540] rounded-lg flex items-center justify-center shrink-0">
                        <Ship className="h-5 w-5 sm:h-6 sm:w-6 text-[#D4AF37]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base text-[#0A2540] truncate">{cruise.name}</h3>
                        <div className="flex items-center gap-3 text-xs sm:text-sm text-[#4A5568] flex-wrap">
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{cruise.destination}</span>
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{cruise.duration}</span>
                        </div>
                        <div className="flex gap-1.5 sm:gap-2 mt-1 flex-wrap">
                          {cruise.is_cabin_booking && <span className="px-1.5 sm:px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] sm:text-xs rounded">Cab: {cruise.base_price}€</span>}
                          {cruise.is_privatization && <span className="px-1.5 sm:px-2 py-0.5 bg-[#0A2540]/10 text-[#0A2540] text-[10px] sm:text-xs rounded">Privat: {cruise.privatization_base_price}€</span>}
                          {!cruise.is_active && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded">Inactif</span>}
                          <span className="px-1.5 sm:px-2 py-0.5 bg-[#2A9D8F]/10 text-[#2A9D8F] text-[10px] sm:text-xs rounded">
                            {departures[cruise.slug]?.length || 0} dep.
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => toggleExpand(cruise)} data-testid={`expand-departures-${cruise.slug}`}>
                        {expandedCruise === cruise.slug ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                        Departs
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => handleEdit(cruise)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700" onClick={() => handleDuplicate(cruise.id)} title="Dupliquer"><Copy className="h-4 w-4" /></Button>
                      <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => handleDelete(cruise.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  {/* Departures panel */}
                  {expandedCruise === cruise.slug && (
                    <div className="border-t border-[#E2E8F0] bg-[#FAFBFC] p-4 space-y-3" data-testid={`departures-panel-${cruise.slug}`}>
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm text-[#0A2540]">Dates de départ</h4>
                        <Button size="sm" className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white"
                          onClick={() => setAddingDeparture(addingDeparture === cruise.slug ? null : cruise.slug)}
                          data-testid={`add-departure-${cruise.slug}`}>
                          <Plus className="h-4 w-4 mr-1" />Ajouter un départ
                        </Button>
                      </div>

                      {/* Add departure form */}
                      {addingDeparture === cruise.slug && (
                        <div className="bg-white border-2 border-[#C4724F]/30 rounded-lg p-4 space-y-3" data-testid="new-departure-form">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div><Label className="text-xs">Date début *</Label><Input type="date" value={newDep.start_date} onChange={e => setNewDep(f => ({ ...f, start_date: e.target.value }))} /></div>
                            <div><Label className="text-xs">Date fin *</Label><Input type="date" value={newDep.end_date} onChange={e => setNewDep(f => ({ ...f, end_date: e.target.value }))} /></div>
                            <div><Label className="text-xs">Prix cabine (€/pers)</Label><Input type="number" placeholder={`${cruise.base_price}`} value={newDep.cabin_price} onChange={e => {
                              const val = e.target.value;
                              setNewDep(f => ({ ...f, cabin_price: val, privatization_price: String(Math.round(parseFloat(val || 0) * 8)) }));
                            }} /></div>
                            <div><Label className="text-xs">Prix privatisation (€) <span className="text-[#94A3B8]">= x8</span></Label><Input type="number" placeholder={`${cruise.privatization_base_price}`} value={newDep.privatization_price} onChange={e => setNewDep(f => ({ ...f, privatization_price: e.target.value }))} /></div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div><Label className="text-xs">Cabines dispo</Label><Input type="number" min="0" max="10" value={newDep.available_cabins} onChange={e => setNewDep(f => ({ ...f, available_cabins: e.target.value }))} /></div>
                            <div><Label className="text-xs">Catamaran</Label><Input value={newDep.catamaran} onChange={e => setNewDep(f => ({ ...f, catamaran: e.target.value }))} /></div>
                            <div className="flex items-end gap-2 col-span-2">
                              <Button size="sm" className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white" onClick={() => handleAddDeparture(cruise)} disabled={!newDep.start_date || !newDep.end_date}>
                                Ajouter
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setAddingDeparture(null)}>Annuler</Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Departures list */}
                      {(departures[cruise.slug] || []).length > 0 ? (
                        <div className="space-y-2">
                          {departures[cruise.slug].map(dep => (
                            <DepartureRow key={dep.id} dep={dep} token={token}
                              onUpdate={() => fetchDepartures(cruise.slug)}
                              onDelete={() => fetchDepartures(cruise.slug)} />
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-[#94A3B8] text-sm">
                          Aucun départ programmé. Cliquez sur "Ajouter un départ" pour commencer.
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCruises;
